import { canUseDOM, getBaseUrl } from '@common/utils'
import accounting from 'accounting'
import striptags from 'striptags'
import cookie from './cookie'

const noop = () => {}
const unformatPrice = n => accounting.unformat(n)
const sanitizeHtml = s => striptags(s)

const IN_STOCK = 'In Stock'
const OUT_OF_STOCK = 'Out of Stock'
const BRAND = 'Iqos'

export class AnalyticsManager {
  constructor(dataLayer) {
    this.dataLayer = dataLayer || []
  }

  productClickBundle = (bundle, bundlePostion, list, url) => {
    const dataLayer = this.dataLayer
    const hasStock =
      bundle.products &&
      bundle.products.every(p => p.options.every(o => o.inStock))

    const data = {
      event: 'productClick',
      ecommerce: {
        click: {
          actionField: {
            list: list
          },
          products: [
            {
              name: bundle.name ? sanitizeHtml(bundle.name) : '',
              id: bundle.id,
              price: unformatPrice(bundle.price),
              category: bundle.categoryId,
              position: bundlePostion + 1,
              dimension52: bundle.products
                ? hasStock
                  ? IN_STOCK
                  : OUT_OF_STOCK
                : undefined,
              metric11: unformatPrice(bundle.price)
            }
          ]
        }
      },
      eventCallback: function () {
        if (url) {
          document.location = getBaseUrl() + url
        }
      }
    }
    dataLayer ? dataLayer.push(data) : noop()
  }

  productClick = (product, selectedOptionIndex, productPostion, list) => {
    const dataLayer = this.dataLayer
    const selectedProduct = product.options[selectedOptionIndex]

    const data = {
      event: 'productClick',
      ecommerce: {
        click: {
          actionField: {
            list: list
          },
          products: [
            {
              id: product.id,
              name: product.name ? sanitizeHtml(product.name) : '',
              price: unformatPrice(selectedProduct.price),
              brand: BRAND,
              category: sanitizeHtml(product.category),
              position: productPostion + 1,
              variant: selectedProduct.variantName
                ? sanitizeHtml(selectedProduct.variantName)
                : undefined,
              dimension51: sanitizeHtml(product.category),
              dimension52: Object.prototype.hasOwnProperty.call(
                selectedProduct,
                'inStock'
              )
                ? selectedProduct.inStock
                  ? IN_STOCK
                  : OUT_OF_STOCK
                : undefined,
              dimension54: !!product.products,
              metric11: unformatPrice(selectedProduct.listPrice)
            }
          ]
        }
      },
      user: {
        spiceID: cookie.getSpiceId() || ''
      }
    }
    dataLayer ? dataLayer.push(data) : noop()
  }

  productImpression = (product, selectedOptionIndex, productPostion, list) => {
    const dataLayer = this.dataLayer

    const selectedProduct = product.options[selectedOptionIndex]
    const impressions = [
      {
        id: product.id,
        name: product.name ? sanitizeHtml(product.name) : '',
        price: unformatPrice(selectedProduct.price),
        brand: BRAND,
        category: sanitizeHtml(product.category),
        position: productPostion + 1,
        list: list,
        variant: selectedProduct.variantName
          ? sanitizeHtml(selectedProduct.variantName)
          : undefined,
        dimension51: sanitizeHtml(product.category),
        dimension52: Object.prototype.hasOwnProperty.call(
          selectedProduct,
          'inStock'
        )
          ? selectedProduct.inStock
            ? IN_STOCK
            : OUT_OF_STOCK
          : undefined,
        dimension54: !!product.products,
        metric11: unformatPrice(selectedProduct.listPrice)
      }
    ]

    const data = {
      event: 'productImpression',
      ecommerce: {
        currencyCode: 'JPY',
        impressions: impressions
      },
      user: {
        spiceID: cookie.getSpiceId() || ''
      }
    }
    dataLayer ? dataLayer.push(data) : noop()
  }

  productListImpression = (products, listName) => {
    const dataLayer = this.dataLayer

    const impressions = products.map((product, index) => {
      const selectedProduct = product.options
        ? product.options[product.selectedOptionIndex]
        : product
      return {
        id: product.id,
        name: product.name ? sanitizeHtml(product.name) : '',
        price: unformatPrice(selectedProduct.price),
        brand: BRAND,
        category: sanitizeHtml(product.category),
        position: index + 1,
        list: listName,
        variant: selectedProduct.variantName
          ? sanitizeHtml(selectedProduct.variantName)
          : undefined,
        dimension51: sanitizeHtml(product.category),
        dimension52: Object.prototype.hasOwnProperty.call(
          selectedProduct,
          'inStock'
        )
          ? selectedProduct.inStock
            ? IN_STOCK
            : OUT_OF_STOCK
          : undefined,
        dimension54: !!product.products,
        metric11: unformatPrice(selectedProduct.listPrice)
      }
    })

    // Split impressions by 10 (or whatever to avoid to large payload)
    var i

    var j

    var chunk = 10
    for (i = 0, j = impressions.length; i < j; i += chunk) {
      const temp = impressions.slice(i, i + chunk)
      const data = {
        event: 'productImpression',
        ecommerce: {
          currencyCode: 'JPY',
          impressions: temp
        },
        user: {
          spiceID: cookie.getSpiceId() || ''
        }
      }
      dataLayer ? dataLayer.push(data) : noop()
    }
  }

  productDetailsView = (product, selectedOptionIndex) => {
    const dataLayer = this.dataLayer
    const selectedProduct =
      product &&
      (selectedOptionIndex || selectedOptionIndex === 0) &&
      product.options[selectedOptionIndex]

    const data = {
      event: 'productDetailsView',
      ecommerce: {
        detail: {
          products: selectedProduct && [
            {
              id: product.product.id,
              name: selectedProduct.variantName
                ? sanitizeHtml(selectedProduct.variantName)
                : '',
              price: unformatPrice(selectedProduct.price),
              brand: BRAND,
              category: sanitizeHtml(product.product.category),
              variant: selectedProduct.variantId
            }
          ]
        }
      },
      user: {
        spiceID: cookie.getSpiceId() || ''
      }
    }

    dataLayer ? dataLayer.push(data) : noop()
  }

  productDetailSelection = productName => {
    const dataLayer = this.dataLayer

    const data = {
      event: 'gtmZoneEvent',
      eventCategory: 'Product selector',
      eventAction: 'Click',
      eventLabel: productName,
      eventValue: 0,
      eventNonInteraction: true
    }

    dataLayer ? dataLayer.push(data) : noop()
  }

  addToCartBundle = bundle => {
    const dataLayer = this.dataLayer

    const data = {
      event: 'addToCart',
      ecommerce: {
        currencyCode: 'JPY',
        add: {
          products: [
            {
              name: bundle.name ? sanitizeHtml(bundle.name) : '',
              id: bundle.id,
              price: unformatPrice(bundle.price),
              category: bundle.categoryId,
              quantity: 1,
              dimension52: IN_STOCK, // availability
              metric11: unformatPrice(bundle.price)
            }
          ]
        }
      }
    }

    dataLayer ? dataLayer.push(data) : noop()
  }

  addToCart = (product, selectedOptionIndex, quantity) => {
    const dataLayer = this.dataLayer
    let selectedProduct = null

    if (product.options) {
      selectedProduct = product.options[selectedOptionIndex]
    } else if (product.products) {
      selectedProduct = product.products[selectedOptionIndex]
    }

    if (!selectedProduct) {
      return
    }

    const data = {
      event: 'addToCart',
      ecommerce: {
        currencyCode: 'JPY',
        add: {
          products: [
            {
              id: product.id,
              name: selectedProduct.variantName
                ? sanitizeHtml(selectedProduct.variantName)
                : '',
              price: unformatPrice(selectedProduct.price),
              brand: BRAND,
              category: sanitizeHtml(product.category),
              quantity: quantity,
              variant: selectedProduct.variantId
            }
          ]
        }
      },
      user: {
        spiceID: cookie.getSpiceId() || ''
      }
    }

    dataLayer ? dataLayer.push(data) : noop()
  }

  addToCartLimitations = (id = '', name = '', cartLimitation = false) => {
    const dataLayer = this.dataLayer

    const data = {
      event: 'gtmZoneEvent',
      eventCategory: 'Purchase Limitations',
      eventAction: 'Display',
      eventLabel: cartLimitation
        ? `Error - Limitation in cart - ${id} - ${name}`
        : `Error - Limitation in purchase history - ${id} - ${name}`,
      eventValue: 0,
      eventNonInteraction: true
    }

    dataLayer ? dataLayer.push(data) : noop()
  }

  removeFromCart = item => {
    const dataLayer = this.dataLayer

    const data = {
      event: 'removeFromCart',
      ecommerce: {
        currencyCode: 'JPY',
        remove: {
          products: [
            {
              name: item.name ? sanitizeHtml(item.name) : '',
              id: item.id,
              price: unformatPrice(item.price),
              brand: BRAND,
              category: item.category ? sanitizeHtml(item.category) : '',
              quantity: item.amount,
              variant: item.options[item.selectedOptionIndex].variantId
            }
          ]
        }
      },
      user: {
        spiceID: cookie.getSpiceId() || ''
      }
    }

    dataLayer ? dataLayer.push(data) : noop()
  }

  updateCartQty = (oldQty, newQty, item) => {
    const dataLayer = this.dataLayer

    let event = ''
    let child = ''
    let diff = 0
    if (oldQty < newQty) {
      event = 'addToCart'
      child = 'add'
      diff = newQty - oldQty
    } else if (oldQty > newQty) {
      event = 'removeFromCart'
      child = 'remove'
      diff = oldQty - newQty
    }

    const data = {
      event: event,
      ecommerce: {
        currencyCode: 'JPY',
        [child]: {
          products: [
            {
              name: item.name ? sanitizeHtml(item.name) : '',
              id: item.id,
              price: unformatPrice(item.price),
              brand: BRAND,
              category: item.category ? sanitizeHtml(item.category) : '',
              quantity: diff,
              variant: item.options[0].variantId
            }
          ]
        }
      },
      user: {
        spiceID: cookie.getSpiceId() || ''
      }
    }
    dataLayer ? dataLayer.push(data) : noop()
  }

  virtualPageView = (pageName, pageCategory) => {
    const dataLayer = this.dataLayer

    const data = {
      event: 'virtualPageView',
      page: {
        pageInfo: {
          pageName: pageName
        },
        category: {
          primaryCategory: pageCategory
        }
      },
      user: {
        spiceID: cookie.getSpiceId() || ''
      }
    }
    dataLayer ? dataLayer.push(data) : noop()
  }

  checkoutView = (products, step, option, eventLabel) => {
    const dataLayer = this.dataLayer

    const formatedProduct = products
      .filter(product => !product.isLogistics)
      .map(product => {
        return {
          name: sanitizeHtml(product.name),
          id: product.id,
          price: unformatPrice(product.price),
          brand: BRAND,
          category: sanitizeHtml(product.category),
          quantity: product.amount,
          dimension51: '', // product version
          dimension52: IN_STOCK, // availability
          metric11: unformatPrice(product.basePrice), // list price
          variant:
            product.options.length > 0 ? product.options[0].variantId : null
        }
      })

    const data = {
      event: 'checkout',
      ecommerce: {
        checkout: {
          actionField: { step: step, option: option },
          products: formatedProduct
        }
      },
      ...(eventLabel && { eventLabel }),
      user: {
        spiceID: cookie.getSpiceId() || ''
      }
    }
    if (data.ecommerce.checkout.actionField.option === null) {
      delete data.ecommerce.checkout.actionField.option
    }

    dataLayer ? dataLayer.push(data) : noop()
  }

  checkoutOption = (step, checkoutOption) => {
    const dataLayer = this.dataLayer

    const data = {
      event: 'checkoutOption',
      ecommerce: {
        checkout_option: {
          actionField: {
            step: step,
            option: checkoutOption // enter the new value (for shipping, standard, express, overnight, cash on delivery, ...)
          }
        }
      },
      user: {
        spiceID: cookie.getSpiceId() || ''
      }
    }

    dataLayer ? dataLayer.push(data) : noop()
  }

  purchaseOption = (cart, orderId, coupon, orderType, paymentMethod) => {
    const dataLayer = this.dataLayer
    const products = cart.products
    const formatedProducts = products
      .filter(product => !product.isLogistics)
      .map(product => {
        return {
          name: sanitizeHtml(product.name),
          id: product.id,
          price: unformatPrice(product.price),
          brand: BRAND,
          category: sanitizeHtml(product.category),
          quantity: product.amount,
          dimension51: '', // product version
          dimension52: IN_STOCK, // availability
          metric11: unformatPrice(product.basePrice), // list price
          variant:
            product.options.length > 0 ? product.options[0].variantId : null
        }
      })

    const data = {
      event: 'purchase',
      purchaseData: {
        giftCard: '0',
        orderType
      },
      ecommerce: {
        purchase: {
          actionField: {
            id: orderId,
            affiliation: 'JP',
            revenue: unformatPrice(cart.total),
            tax: '0',
            shipping: cart.shipping_cost,
            coupon,
            paymentMethod
          },
          products: formatedProducts
        }
      },
      user: {
        spiceID: cookie.getSpiceId() || ''
      }
    }
    dataLayer ? dataLayer.push(data) : noop()
  }

  alreadyHasDevice = alreadyHasDevice => {
    const dataLayer = this.dataLayer

    const data = {
      event: 'gtmZoneEvent', // Do not modify this line
      eventCategory: 'Registration',
      eventAction: 'Used tobacco product',
      eventLabel: alreadyHasDevice, // Replace xxx by the english name of the selected product
      eventValue: 0,
      eventNonInteraction: true
    }

    dataLayer ? dataLayer.push(data) : noop()
  }

  formLogin = loginStatus => {
    const dataLayer = this.dataLayer

    const data = {
      event: 'formLogin',
      formLoginResult: loginStatus,
      user: {
        spiceID: cookie.getSpiceId() || ''
      }
    }

    dataLayer ? dataLayer.push(data) : noop()
  }

  loginModalShown = () => {
    const dataLayer = this.dataLayer

    const data = {
      event: 'gtmZoneEvent',
      eventCategory: 'Login',
      eventAction: 'Form displayed',
      eventLabel: '',
      eventValue: 0,
      eventNonInteraction: true
    }

    dataLayer ? dataLayer.push(data) : noop()
  }

  logout = spiceId => {
    const dataLayer = this.dataLayer

    const data = {
      event: 'formLogin',
      formLoginResult: 'logout',
      user: {
        spiceID: spiceId || ''
      }
    }

    dataLayer ? dataLayer.push(data) : noop()
  }

  systemError = (errorType, errorMessage) => {
    const dataLayer = this.dataLayer

    const data = {
      event: 'systemError',
      errorType: errorType, // cf. table below
      errorMessage: errorMessage, // cf.table of below
      user: {
        spiceID: cookie.getSpiceId() || ''
      }
    }

    dataLayer ? dataLayer.push(data) : noop()
  }

  onBoarding = (step, category, contains) => {
    const dataLayer = this.dataLayer

    const data = {
      event: 'onboardingStep',
      onboardingStep: step,
      onboardingStepCategory: sanitizeHtml(category),
      onboardingStepContains: contains || '',
      user: {
        spiceID: cookie.getSpiceId() || ''
      }
    }
    dataLayer ? dataLayer.push(data) : noop()
  }

  onRegistrationLoad = () => {
    const dataLayer = this.dataLayer

    const data = {
      event: 'onboardingStep',
      onboardingStep: 'Start',
      onboardingStepCategory: 'Account',
      onboardingStepContains:
        'email, password, confirm-password, terms, marketing opt-in'
    }

    dataLayer ? dataLayer.push(data) : noop()
  }

  onRegistrationOCRError = result => {
    const dataLayer = this.dataLayer

    const data = {
      event: 'gtmZoneEvent',
      eventCategory: 'Error',
      eventAction: 'OCR',
      eventLabel: result,
      eventValue: 0,
      eventNonInteraction: true,
      user: {
        spiceID: cookie.getSpiceId() || ''
      }
    }

    dataLayer ? dataLayer.push(data) : noop()
  }

  onBoardingError = result => {
    const dataLayer = this.dataLayer

    const data = {
      event: 'payCasso',
      payCassoResult: result,
      user: {
        spiceID: cookie.getSpiceId() || ''
      }
    }

    dataLayer ? dataLayer.push(data) : noop()
  }

  genericEvent = e => {
    const dataLayer = this.dataLayer

    const data = {
      event: e,
      user: {
        spiceID: cookie.getSpiceId() || ''
      }
    }

    dataLayer ? dataLayer.push(data) : noop()
  }

  genericError = err => {
    console.log('Error ' + err)
  }

  addDevice = () => {
    const dataLayer = this.dataLayer

    const data = {
      event: 'deviceAction',
      deviceAction: 'Add',
      user: {
        spiceID: cookie.getSpiceId() || ''
      }
    }

    dataLayer ? dataLayer.push(data) : noop()
  }

  registrationPhoneVerification = () => {
    const dataLayer = this.dataLayer

    const data = {
      event: 'gtmZoneEvent', // Do not modify this line
      eventCategory: 'Registration',
      eventAction: 'Step - Phone Verification',
      eventLabel: 'JP',
      eventValue: 0,
      eventNonInteraction: true
    }

    dataLayer ? dataLayer.push(data) : noop()
  }

  clickGFCoupon = coupon => {
    const dataLayer = this.dataLayer

    const data = {
      memberGetMemberAction: 'RedeemCoupon',
      event: 'memberGetMember',
      eventCategory: 'GFG',
      eventLabel: coupon,
      user: {
        spiceID: cookie.getSpiceId() || ''
      }
    }

    dataLayer ? dataLayer.push(data) : noop()
  }

  clickGFCoins = () => {
    const dataLayer = this.dataLayer

    const data = {
      memberGetMemberAction: 'RedeemCoins',
      event: 'memberGetMember',
      eventCategory: 'GFG',
      eventLabel: 'Coins',
      user: {
        spiceID: cookie.getSpiceId() || ''
      }
    }

    dataLayer ? dataLayer.push(data) : noop()
  }

  couponAplicationResult = (success, label) => {
    const dataLayer = this.dataLayer

    const data = {
      event: 'formPromoCoupon',
      formPromoCouponResult: success ? 'success' : 'failed',
      formPromoCouponLabel: label
    }
    dataLayer ? dataLayer.push(data) : noop()
  }

  productFamilyConflict = getCart => {
    const dataLayer = this.dataLayer

    const data = {
      event: 'productFamilyIncompatibility',
      eventCategory: 'Incompatible Devices',
      eventAction: 'Display',
      eventLabel: getCart
        ? 'Cart – Incompatible Devices Warning'
        : 'Add to Cart - Pop-up – Incompatibility Error'
    }
    dataLayer ? dataLayer.push(data) : noop()
  }

  productFamilyConflictDecision = isGoingToCart => {
    const dataLayer = this.dataLayer

    const data = {
      event: 'productFamilyIncompatibilityDecision',
      eventCategory: 'Incompatible Devices',
      eventAction: 'Click',
      eventLabel: isGoingToCart
        ? 'Add to Cart - Proceed checkout'
        : 'Add to Cart - Continue shopping'
    }
    dataLayer ? dataLayer.push(data) : noop()
  }

  notifyMe = (action, label, id, name) => {
    const dataLayer = this.dataLayer
    const fullLabel =
      label + `${id ? ' - ' + id : ''}` + `${name ? ' - ' + name : ''}`

    const data = {
      event: 'gtmZoneEvent',
      eventCategory: 'Notify Me',
      eventAction: action,
      eventLabel: fullLabel,
      eventValue: 0,
      eventNonInteraction: true
    }

    dataLayer ? dataLayer.push(data) : noop()
  }

  viewQoinsHistoryClick = () => {
    const dataLayer = this.dataLayer
    const data = {
      event: 'gtmZoneEvent',
      eventCategory: 'RAF to PAYG',
      eventAction: 'Click',
      eventLabel: 'View Qoins History',
      eventValue: 0,
      eventNonInteraction: true
    }
    dataLayer ? dataLayer.push(data) : noop()
  }

  samplingCart = (action, label) => {
    const dataLayer = this.dataLayer
    const data = {
      event: 'gtmZoneEvent',
      eventCategory: 'Omnichannel Sampling',
      eventAction: action,
      eventLabel: label,
      eventValue: 0,
      eventNonInteraction: true
    }
    dataLayer ? dataLayer.push(data) : noop()
  }

  tasteSelector = label => {
    const dataLayer = this.dataLayer
    const data = {
      event: 'gtmZoneEvent',
      eventCategory: 'Taste Matching',
      eventAction: 'Click',
      eventLabel: label,
      eventValue: 0,
      eventNonInteraction: true
    }
    dataLayer ? dataLayer.push(data) : noop()
  }

  tradeInEvent = (step, deviceName) => {
    const dataLayer = this.dataLayer
    const data = {
      event: 'tradeIn',
      tradeInDiagnosticStep: step,
      tradeInDiagnosticProduct: deviceName
    }
    dataLayer ? dataLayer.push(data) : noop()
  }

  registrationEvent = (action, label) => {
    const dataLayer = this.dataLayer

    const data = {
      event: 'gtmZoneEvent',
      eventCategory: 'Registration',
      eventAction: action,
      eventLabel: label || 'JP_Local',
      eventValue: 0,
      eventNonInteraction: true
    }

    dataLayer ? dataLayer.push(data) : noop()
  }
}

export default new AnalyticsManager(canUseDOM ? window.dataLayer : null) // set by google tag manager
